import {
    Button,
    createTheme,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    Grid,
    IconButton,
    TableSortLabel,
    ThemeProvider,
    Typography,
} from "@material-ui/core";
import { Archive, MenuBook } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import ReactHtmlParser from "html-react-parser";
import queryString from "query-string";
import React, { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { listContracts as listProviderContracts } from "../api/ProviderContractAPI";
import { deleteContractById, getContract, getDocumentUrl, listContracts } from "../api/TenantContractAPI";
import { formatDate } from "../components/DateFormat";
import DialogPopup from "../components/DialogPopup";
import Gridable from "../components/Gridable";
import { MoreVertIcon } from "../components/Icons";
import Pagination, { getOffset, initialPage, initialPageSize } from "../components/Pagination";
import { ErrorSnackbar, SuccessSnackbar, WarningSnackbar } from "../components/Snackbars";
import ValidatedInput from "../components/ValidatedInput";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import { Contract, Contracts, ContractsQueryParams, ContractStatus } from "../model/Contract";
import { isRoleOrParent } from "../model/Role";
import { TenantContractForm } from "./ContractForm";
import ContractMenu from "./ContractMenu";

type Order = "asc" | "desc";

interface ContractsViewProps {
    subtitle: string;
    icon: ReactNode;
    statuses: ContractStatus[];
    showGeneralActions: boolean;
    showRowMenu: boolean;
}

export function ActiveContractsView() {
    return (
        <ContractsView
            subtitle="se_contracts.view.subtitle.active"
            icon={<MenuBook />}
            statuses={[ContractStatus.TO_START, ContractStatus.VALID, ContractStatus.PENDING_DOCS]}
            showGeneralActions={true}
            showRowMenu={true}
        />
    );
}

export function ArchiveContractsView() {
    return (
        <ContractsView
            subtitle="se_contracts.view.subtitle.archive"
            icon={<Archive />}
            statuses={[
                ContractStatus.TO_START,
                ContractStatus.VALID,
                ContractStatus.PENDING_DOCS,
                ContractStatus.CLOSED,
            ]}
            showGeneralActions={false}
            showRowMenu={false}
        />
    );
}

function ContractsView(props: ContractsViewProps) {
    const context = useContext(AppContext);
    const history = useHistory();
    const isProvider = isRoleOrParent(context.session!.role, "provider");
    const canReadProviderContracts = context.isGranted("ProviderContractsRead");
    const canReadTenantContracts = context.isGranted("TenantContractsRead");

    const showRowMenu = props.showRowMenu && !isProvider;

    const [status, setStatus] = useState<string>("loading");
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const [warning, setWarning] = useState<string>();

    const qs = queryString.parse(window.location.search);
    const paramsFromQueryString = (): ContractsQueryParams => {
        const search = qs["search"] ? qs["search"] : "";
        const page = qs["page"] ? parseInt(qs["page"] as string) : initialPage();
        const pageSize = qs["page_size"] ? parseInt(qs["page_size"] as string) : initialPageSize();
        return {
            search: search as string,
            pageSize: pageSize,
            offset: getOffset(page, pageSize),
            statuses: props.statuses,
            startDate: "",
            endDate: "",
        };
    };
    const [workingParams, setWorkingParams] = useState<ContractsQueryParams>(paramsFromQueryString);
    const [params, setParams] = useState<ContractsQueryParams>(paramsFromQueryString);

    const [contracts, setContracts] = useState<Contracts>();

    const DESCRIPTION_COLUMN = translate("se_contracts.view.columns.description") as string;
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<string>();

    const [gridAnchorEl, setGridAnchorEl] = useState<null | HTMLElement>(null);
    const [contract, setContract] = useState<Contract>();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showEditCreateDialog, setShowEditCreateDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSort = (property: keyof Contract, isAsc: boolean) => {
        setContracts((current) => {
            if (!current) {
                return current;
            }
            const sortedItems = [...current.items].sort((a, b) => {
                if ((a[property] ?? "") < (b[property] ?? "")) {
                    return isAsc ? -1 : 1;
                }
                if ((a[property] ?? "") > (b[property] ?? "")) {
                    return isAsc ? 1 : -1;
                }
                return 0;
            });
            return {
                ...current,
                items: sortedItems,
            };
        });
    };

    const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
        if (property === translate("se_contracts.view.columns.description")) {
            handleSort("description", isAsc);
        }
    };

    const fetchContracts = useCallback(() => {
        setStatus("loading");
        if (!params) {
            return;
        }
        setOrderBy(undefined);
        setOrder("desc");
        let contractsPromise;
        if (canReadTenantContracts) {
            contractsPromise = listContracts(context.session!.tenant!.id, params);
        } else if (canReadProviderContracts) {
            contractsPromise = listProviderContracts(
                context.session!.tenant!.id,
                context.session!.provider!.id,
                params
            );
        } else {
            return;
        }
        contractsPromise
            .then((response) => {
                setContracts(response);
            })
            .catch((error) => {
                setStatus(error.message);
            })
            .finally(() => {
                setStatus("loaded");
            });
    }, [canReadProviderContracts, canReadTenantContracts, context.session, params]);

    useEffect(() => {
        fetchContracts();
    }, [context.session, fetchContracts, history, params]);

    const onChangedPage = (page: number) => {
        setParams((current) => ({ ...current, offset: getOffset(page, current.pageSize!) }));
        setContracts(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setParams((current) => ({ ...current, pageSize, offset: getOffset(page, pageSize) }));
        setContracts(undefined);
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let qs = queryString.parse(window.location.search);
        qs["search"] = workingParams.search;
        if (workingParams.search === "") {
            delete qs["search"];
        }
        qs["page"] = "1";

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);
        setParams({ ...workingParams, offset: 0 });
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onClickedRow = (item: Contract) => {
        history.push(`/se/contract/${item.id}/details`);
    };

    const onCloseSnackbar = () => {
        setError(undefined);
        setSuccess(undefined);
        setWarning(undefined);
    };

    const getStatus = (status: ContractStatus) => {
        return translate(`se_contracts.status.${status}`) as string;
    };

    const onAddContract = () => {
        if (!context.isGranted("TenantContractsCreate")) {
            return;
        }
        setContract(undefined);
        setShowEditCreateDialog(true);
    };

    const onCloseAddEditDialog = () => {
        setContract(undefined);
        setShowEditCreateDialog(false);
    };

    const onClickedOptions = (item: Contract) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setGridAnchorEl(event.currentTarget);
        setContract(item);
    };

    const onCloseOption = () => {
        setGridAnchorEl(null);
        setContract(undefined);
    };

    const onEditOption = () => {
        if (!contract) return;

        if (!context.isGranted("TenantContractsUpdate")) {
            return;
        }

        setGridAnchorEl(null);
        setShowEditCreateDialog(true);
    };

    const onDeleteOption = () => {
        if (!contract) return;

        if (!context.isGranted("TenantContractsDelete")) {
            return;
        }

        setGridAnchorEl(null);
        setShowDeleteDialog(true);
    };

    const onConfirmDelete = () => {
        if (!contract) return;

        setSubmitting(true);
        deleteContractById(context.session!.tenant!.id, contract.id)
            .then((response) => {
                setSuccess(translate("se_contracts.view.delete.success") as string);
                fetchContracts();
            })
            .catch((error) => {
                setError(error.message);
            })
            .finally(() => {
                setSubmitting(false);
                setShowDeleteDialog(false);
            });
    };

    const sortLabelTheme = createTheme({
        overrides: {
            MuiTableSortLabel: {
                icon: {
                    opacity: 0.4,
                },
            },
        },
    });

    return (
        <>
            <Pagination
                title={translate("se_contracts.view.title") as string}
                subtitle={translate(props.subtitle) as string}
                icon={props.icon}
                page={Math.floor(params.offset! / params.pageSize!) + 1}
                pageSize={params.pageSize || 20}
                count={contracts ? contracts.items.length : 0}
                total={contracts ? contracts.total : 0}
                onChangedPage={onChangedPage}
                onChangedPageSize={onChangedPageSize}
                action={
                    props.showGeneralActions && context.isGranted("TenantContractsCreate") ? (
                        <Grid container alignItems="center" justify="flex-end" spacing={1}>
                            <Grid item xs="auto">
                                <Fab
                                    color="primary"
                                    size="small"
                                    title={translate("buttons.add") as string}
                                    onClick={onAddContract}
                                >
                                    <AddIcon />
                                </Fab>
                            </Grid>
                        </Grid>
                    ) : undefined
                }
            >
                <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                    <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                        <Grid item xs={12} sm={true} md={true} lg>
                            <ValidatedInput
                                type="text"
                                id="search"
                                name="search"
                                label={translate("se_contracts.view.filter") as string}
                                margin="dense"
                                disabled={false}
                                value={params!.search}
                                onValueChanged={onFilterChanged}
                            />
                        </Grid>
                        <Grid item xs="auto">
                            <Button type="submit" variant="outlined" color="secondary" size="medium">
                                {translate("buttons.search")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                <Divider />
                <Gridable
                    items={contracts ? contracts.items : []}
                    loading={status === "loading"}
                    error={status !== "loading" && status !== "loaded" ? status : undefined}
                    empty={translate("providers.empty") as string}
                    onClick={onClickedRow}
                    columns={[
                        {
                            title: translate("se_contracts.view.columns.id") as string,
                            converter: (contract) => contract.sisub,
                            xs: true,
                            sm: true,
                            md: 2,
                            lg: 2,
                            xl: 2,
                        },
                        {
                            title: (
                                <>
                                    <b>{DESCRIPTION_COLUMN}</b>
                                    <ThemeProvider theme={sortLabelTheme}>
                                        <TableSortLabel
                                            active={orderBy === DESCRIPTION_COLUMN}
                                            direction={orderBy === DESCRIPTION_COLUMN ? order : "desc"}
                                            onClick={createSortHandler(DESCRIPTION_COLUMN)}
                                        />
                                    </ThemeProvider>
                                </>
                            ),
                            converter: (contract) => contract.description,
                            xs: true,
                            sm: true,
                            md: true,
                            lg: 3,
                            xl: 3,
                        },
                        {
                            title: translate("se_contracts.view.columns.provider") as string,
                            converter: (contract) => contract.provider?.name || "---",
                            xs: false,
                            sm: true,
                            md: 3,
                            lg: 3,
                            xl: 3,
                        },
                        {
                            title: translate("se_contracts.view.columns.start_date") as string,
                            converter: (contract) =>
                                contract.specialized_services?.from
                                    ? formatDate(contract.specialized_services?.from, "DD/MM/YYYY")
                                    : "---",
                            xs: false,
                            sm: false,
                            md: false,
                            lg: 1,
                            xl: 1,
                        },
                        {
                            title: translate("se_contracts.view.columns.end_date") as string,
                            converter: (contract) =>
                                contract.specialized_services?.to
                                    ? formatDate(contract.specialized_services?.to, "DD/MM/YYYY")
                                    : (translate("se_contracts.view.undefined") as string),
                            xs: false,
                            sm: false,
                            md: false,
                            lg: 1,
                            xl: 1,
                        },
                        {
                            title: translate("se_contracts.view.columns.status") as string,
                            converter: (contract) => getStatus(contract.status),
                            xs: false,
                            sm: false,
                            md: false,
                            lg: true,
                            xl: true,
                        },
                        {
                            title: (
                                <IconButton size="small" style={{ visibility: "hidden" }} disabled>
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            converter: (contract) =>
                                showRowMenu && (
                                    <IconButton
                                        aria-label="options"
                                        color="default"
                                        size="small"
                                        onClick={onClickedOptions(contract)}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                ),
                            justify: "flex-end",
                            xs: showRowMenu,
                        },
                    ]}
                />
                <ErrorSnackbar message={error} onClose={onCloseSnackbar} />
                <SuccessSnackbar message={success} onClose={onCloseSnackbar} />
                <WarningSnackbar message={warning} onClose={onCloseSnackbar} />
                {showRowMenu && gridAnchorEl && contract && (
                    <ContractMenu
                        type="tenant"
                        anchor={gridAnchorEl}
                        contract={contract}
                        onClose={onCloseOption}
                        onDelete={onDeleteOption}
                        onEdit={onEditOption}
                    />
                )}
                {showDeleteDialog && contract && (
                    <DialogPopup
                        open
                        title={translate("se_contracts.view.delete.title")}
                        onClose={() => setShowDeleteDialog(false)}
                        closeColor="default"
                        disableEscapeKeyDown={submitting}
                        disableBackdropClick={submitting}
                        disable={submitting}
                        button={
                            <Button
                                onClick={onConfirmDelete}
                                variant="outlined"
                                color="secondary"
                                disabled={submitting}
                            >
                                {translate("buttons.delete")}
                            </Button>
                        }
                        closeText={translate("buttons.cancel") as string}
                    >
                        {ReactHtmlParser(
                            translate("se_contracts.view.delete.message", {
                                description: contract.description,
                            }) as string
                        )}
                    </DialogPopup>
                )}
            </Pagination>
            {showEditCreateDialog && (
                <Dialog open maxWidth={"xs"}>
                    <DialogTitle>
                        <Typography>{contract ? translate("contracts.edit") : translate("contracts.new")}</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <TenantContractForm
                            type="tenant"
                            isEdit={!!contract}
                            contractId={contract?.id}
                            getter={getContract}
                            documentGetter={getDocumentUrl}
                            onFinishSubmit={(contract) => {
                                history.push(`/se/contract/${contract.id}/details`);
                            }}
                            onCancelSubmit={onCloseAddEditDialog}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
